import { forwardRef, useImperativeHandle } from 'react';
import { FormControl, FormLabel, RadioGroup, FormControlLabel, Radio, Typography, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';

export interface Props {
  answers: Record<string, boolean | null>;
  onChange: (id: string, value: boolean | null) => void;
  isCompleted: boolean;
}

export interface FoodChainQuestionListRef {
  getMappedAnswers: () => Record<string, boolean>;
  getUnansweredQuestions: () => string[];
}

const FoodChainQuestion = forwardRef<FoodChainQuestionListRef, Props>(({ answers, onChange, isCompleted }, ref) => {
  const { t } = useTranslation();

  const questions = [
    {
      id: 'q1',
      label: 'Were the pigs born and raised in the Netherlands?',
      readonly: false,
      defaultValue: null
    },
    {
      id: 'q2',
      label: 'Have the monitoring obligations for ZvA/SVD/Salmonella been met during the last recognition period?',
      readonly: false,
      defaultValue: null
    },
    {
      id: 'q3',
      label:
        'In the past 6 months, have any clinical symptoms of AR, ZvA, PRRS or diseases listed by the OIE (WOAH) been observed on your farm?',
      readonly: false,
      defaultValue: null
    },
    {
      id: 'q4',
      label: 'Is the mortality rate among the fattening pigs on the farm higher than 5%?',
      readonly: false,
      defaultValue: null
    },
    {
      id: 'q5',
      label: 'Have the pigs been treated with medication within 60 days prior to delivery?',
      readonly: true,
      defaultValue: false
    }
  ];

  useImperativeHandle(ref, () => ({
    getMappedAnswers: () =>
      questions.reduce(
        (acc, q) => {
          const value = answers[q.id] ?? q.defaultValue;
          if (value !== null) acc[q.label] = value;
          return acc;
        },
        {} as Record<string, boolean>
      ),

    getUnansweredQuestions: () => questions.filter((q) => (answers[q.id] ?? q.defaultValue) === null).map((q) => q.id)
  }));

  return (
    <>
      {questions.map((q) => {
        const value = answers[q.id] ?? q.defaultValue;

        return (
          <Grid size={{ xs: 12 }} key={q.id}>
            {isCompleted ? (
              <Grid container spacing={1} sx={{ alignItems: 'center' }}>
                <Grid size={{ xs: 11 }}>
                  <Typography>{q.label}</Typography>
                </Grid>
                <Grid size={{ xs: 1 }}>
                  <Typography sx={{ fontWeight: 'bold' }}>
                    {value === null ? '-' : value ? t('f-yes') : t('f-no')}
                  </Typography>
                </Grid>
              </Grid>
            ) : (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <FormLabel>{q.label}</FormLabel>
                <RadioGroup
                  row
                  value={value === null ? '' : value ? 'true' : 'false'}
                  onChange={(e) => {
                    const val = e.target.value;
                    onChange(q.id, val === 'true' ? true : val === 'false' ? false : null);
                  }}
                >
                  <FormControlLabel value="true" control={<Radio />} label={t('f-yes')} disabled={q.readonly} />
                  <FormControlLabel value="false" control={<Radio />} label={t('f-no')} disabled={q.readonly} />
                </RadioGroup>
              </FormControl>
            )}
          </Grid>
        );
      })}
    </>
  );
});

export default FoodChainQuestion;
